import { useCallback, useEffect, useRef, useState } from 'react'

export type AudioTranscriptionStatus = 'idle' | 'recording' | 'transcribing' | 'error'

const DEFAULT_MAX_DURATION_MS = 300_000 // 5 minutes
const DEFAULT_WARNING_THRESHOLD_MS = 60_000 // warn 1 minute before limit

export type TranscribeAudio = (audioBlob: Blob, mimeType: string) => Promise<string>

export type UseAudioTranscriptionOptions = {
  transcribeAudio: TranscribeAudio
  maxDurationMs?: number
  warningThresholdMs?: number
  onTranscription?: (text: string) => void
  onError?: (error: Error) => void
  onWarning?: () => void
  onMaxDurationReached?: () => void
}

export type UseAudioTranscriptionReturn = {
  status: AudioTranscriptionStatus
  isRecording: boolean
  isTranscribing: boolean
  transcription: string | null
  error: string | null
  elapsedMs: number
  remainingMs: number
  maxDurationMs: number
  isNearLimit: boolean
  startRecording: () => Promise<void>
  stopRecording: () => void
  reset: () => void
}

function getSupportedMimeType(): string {
  if (typeof MediaRecorder === 'undefined') {
    return 'audio/webm'
  }

  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/mpeg',
    'audio/ogg;codecs=opus',
  ]

  return types.find((type) => MediaRecorder.isTypeSupported(type)) || 'audio/webm'
}

export function useAudioTranscription(
  options: UseAudioTranscriptionOptions
): UseAudioTranscriptionReturn {
  const {
    transcribeAudio,
    maxDurationMs = DEFAULT_MAX_DURATION_MS,
    warningThresholdMs = DEFAULT_WARNING_THRESHOLD_MS,
    onTranscription,
    onError,
    onWarning,
    onMaxDurationReached,
  } = options

  const [status, setStatus] = useState<AudioTranscriptionStatus>('idle')
  const [transcription, setTranscription] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [elapsedMs, setElapsedMs] = useState(0)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const recordingStartTimeRef = useRef<number | null>(null)
  const warningFiredRef = useRef(false)

  const remainingMs = Math.max(0, maxDurationMs - elapsedMs)
  const isNearLimit = remainingMs <= warningThresholdMs && remainingMs > 0

  const clearTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current)
      timerIntervalRef.current = null
    }
    recordingStartTimeRef.current = null
    warningFiredRef.current = false
  }, [])

  const reset = useCallback(() => {
    setStatus('idle')
    setTranscription(null)
    setError(null)
    setElapsedMs(0)
    chunksRef.current = []
    clearTimer()
  }, [clearTimer])

  const sendToTranscription = useCallback(
    async (audioBlob: Blob, mimeType: string) => {
      setStatus('transcribing')

      try {
        const text = await transcribeAudio(audioBlob, mimeType)

        setTranscription(text)
        setStatus('idle')
        onTranscription?.(text)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Transcription failed'
        setError(errorMessage)
        setStatus('error')
        onError?.(err instanceof Error ? err : new Error(errorMessage))
      }
    },
    [transcribeAudio, onTranscription, onError]
  )

  const startRecording = useCallback(async () => {
    try {
      reset()

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const mimeType = getSupportedMimeType()
      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        clearTimer()
        stream.getTracks().forEach((track) => track.stop())
        streamRef.current = null

        const audioBlob = new Blob(chunksRef.current, { type: mimeType })
        sendToTranscription(audioBlob, mimeType)
      }

      recordingStartTimeRef.current = Date.now()
      timerIntervalRef.current = setInterval(() => {
        if (recordingStartTimeRef.current) {
          const elapsed = Date.now() - recordingStartTimeRef.current
          setElapsedMs(elapsed)
        }
      }, 100)

      mediaRecorder.start()
      setStatus('recording')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to access microphone'
      setError(errorMessage)
      setStatus('error')
      onError?.(err instanceof Error ? err : new Error(errorMessage))
    }
  }, [reset, clearTimer, sendToTranscription, onError])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
  }, [])

  useEffect(() => {
    if (status === 'recording' && isNearLimit && !warningFiredRef.current) {
      warningFiredRef.current = true
      onWarning?.()
    }
  }, [status, isNearLimit, onWarning])

  useEffect(() => {
    if (status === 'recording' && elapsedMs >= maxDurationMs) {
      onMaxDurationReached?.()
      stopRecording()
    }
  }, [status, elapsedMs, maxDurationMs, onMaxDurationReached, stopRecording])

  useEffect(() => {
    return () => {
      clearTimer()
    }
  }, [clearTimer])

  return {
    status,
    isRecording: status === 'recording',
    isTranscribing: status === 'transcribing',
    transcription,
    error,
    elapsedMs,
    remainingMs,
    maxDurationMs,
    isNearLimit,
    startRecording,
    stopRecording,
    reset,
  }
}
